import dayjs from 'dayjs'

/**
 * Checks if a date is valid according to the format
 * @param dateString The string representation of a date
 * @param format The expected format of the date string
 * @returns Whether the date is valid
 */
export const isValidDate = (dateString: string, format: string): boolean => {
    // First check format validity
    if (!dayjs(dateString, format, true).isValid()) {
        return false
    }

    // Then check logical validity (e.g., Feb 31 would pass format but is invalid)
    const date = dayjs(dateString, format)

    // Check if the parsed date, when formatted back, gives the same string
    // This catches cases like 31/02/2023 which would be parsed as 03/03/2023
    return date.format(format) === dateString
}

/**
 * Formats a date string with automatic slash insertion
 * @param value The raw user input
 * @param dateFormat The expected date format
 * @returns The formatted date string
 */
export const formatDateInput = (value: string, dateFormat: string): string => {
    // Strip all non-digits
    const digits = value.replace(/\D/g, '')

    // Handle DD/MM/YYYY format (or similar variations)
    if (dateFormat.startsWith('D')) {
        if (digits.length === 0) return ''

        // Day part
        let day = digits.substring(0, 2)
        // Ensure day is between 1-31
        if (day.length === 1) {
            // Allow typing single digit
        } else if (day.length === 2) {
            const dayNum = parseInt(day, 10)
            if (dayNum === 0) day = '01'
            else if (dayNum > 31) day = '31'
        }

        if (digits.length <= 2) return day

        // Month part
        let month = digits.substring(2, 4)
        // Ensure month is between 1-12
        if (month.length === 1) {
            // Allow typing single digit
        } else if (month.length === 2) {
            const monthNum = parseInt(month, 10)
            if (monthNum === 0) month = '01'
            else if (monthNum > 12) month = '12'
        }

        if (digits.length <= 4) return `${day}/${month}`

        // Year part (allow typing as many digits as they want, we'll truncate)
        const year = digits.substring(4, 8)

        return `${day}/${month}/${year}`
    }

    // Handle other formats if needed in the future
    return value
}
